import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ orderId?: string }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;
    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface PodImage {
    podId: string;
    imageType: "PhotoofDeliveredProduct" | "Signature";
    image: string | null;
    url: string;
}

export interface DisplayPodImagesStyles {
    parentWrapper?: InjectableCss;
    podImagesWrapper?: InjectableCss;
    productPhoto?: InjectableCss;
    deliverdProductText?: InjectableCss;
    photoGallary?: GridContainerProps;
    productImage?: GridItemProps;
    podImage?: InjectableCss;
    signature?: InjectableCss;
    signatureImage?: InjectableCss;
    sectionTitle?: TypographyProps;
}

export const displayPodImagesStyles: DisplayPodImagesStyles = {
    parentWrapper: {
        css: css`
            @media screen {
                display: none !important;
            }
            @media print {
                display: block !important;
            }
        `,
    },
    podImagesWrapper: {
        css: css`
            margin: 10px 0px;
        `,
    },
    productPhoto: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    deliverdProductText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    photoGallary: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin: 0px;
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0px;
            }
        `,
    },
    productImage: {
        width: [12, 12, 12, 12, 12],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    podImage: {
        css: css`
            max-width: 177px;
            max-height: 236px;
            width: 100%;
            height: 100%;
        `,
    },
    signature: {
        css: css`
            width: 50%;
        `,
    },
    signatureImage: {
        css: css`
            max-height: 70px;
            height: 100%;
            width: 100%;
            img {
                overflow: hidden;
            }
        `,
    },
    sectionTitle: {
        weight: 500,
        size: 15,
        css: css`
            display: block;
            text-align: left;
        `,
    },
};

const styles = displayPodImagesStyles;

const DisplayPodImages: React.FunctionComponent<Props> = ({ orderData, pageType }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;
    const isShareEntity = getIsShareEntity();
    if (!isShareEntity || !order || !order.properties.podimages) {
        return null;
    }

    const descriptionOrCode = (order.shipViaDescription ?? order.shipCode ?? "").trim().toLowerCase();
    const isWillCall =
        descriptionOrCode.length > 0 && (descriptionOrCode === "will call" || descriptionOrCode === "willcall");

    const getImageSrc = (imageData: string | null, isWillCall: boolean): string => {
        if (!imageData) {
            return "";
        }

        if (isWillCall) {
            return `data:image/webp;base64,${imageData}`;
        }

        // Fallback: assume it's hex, convert to blob URL
        const input = imageData.replace(/[^A-Fa-f0-9]/g, "");
        const binary = [];
        for (let i = 0; i < input.length / 2; i++) {
            const h = input.substr(i * 2, 2);
            binary[i] = parseInt(h, 16);
        }
        const byteArray = new Uint8Array(binary);
        const blob = new Blob([byteArray], { type: "application/octet-stream" });
        return window.URL.createObjectURL(blob);
    };

    let podImageArray: PodImage[] = [];

    try {
        podImageArray = JSON.parse(order.properties?.podimages ?? "[]");
        if (!Array.isArray(podImageArray)) {
            podImageArray = [];
        }
    } catch {
        podImageArray = [];
    }

    const imagesByPodId = podImageArray.reduce<Record<string, PodImage[]>>((acc, item) => {
        (acc[item.podId] ??= []).push(item);
        return acc;
    }, {});

    return (
        <StyledWrapper className="podImages-wrapper" {...styles.parentWrapper}>
            {Object.entries(imagesByPodId).map(([podId, podImages]) => {
                const productPhotos = podImages.filter(img => img.imageType === "PhotoofDeliveredProduct");
                const signatures = podImages.filter(img => img.imageType === "Signature");

                return (
                    <StyledWrapper key={podId} {...styles.podImagesWrapper}>
                        {/* Render product photos if they exist */}
                        {productPhotos.length > 0 && (
                            <StyledWrapper {...styles.productPhoto}>
                                <StyledWrapper {...styles.deliverdProductText}>
                                    <Typography {...styles.sectionTitle}>DELIVERED PRODUCT PHOTO:</Typography>
                                </StyledWrapper>
                                <GridContainer {...styles.photoGallary}>
                                    {productPhotos.map((item, index) => (
                                        <GridItem {...styles.productImage} key={index}>
                                            <LazyImage
                                                src={item.url || getImageSrc(item.image, isWillCall)}
                                                {...styles.podImage}
                                                isShareEntity={isShareEntity}
                                            />
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            </StyledWrapper>
                        )}

                        {/* Render signatures if they exist */}
                        {signatures.length > 0 && (
                            <StyledWrapper {...styles.signature}>
                                <StyledWrapper>
                                    <Typography {...styles.sectionTitle}>DELIVERY SIGNATURE:</Typography>
                                </StyledWrapper>
                                <GridContainer {...styles.photoGallary}>
                                    {signatures.map((item, index) => (
                                        <GridItem
                                            {...styles.productImage}
                                            key={index}
                                            className="watermarked"
                                            data-watermark="watermark.com"
                                        >
                                            <LazyImage
                                                src={item.url || getImageSrc(item.image, isWillCall)}
                                                {...styles.signatureImage}
                                                isShareEntity={isShareEntity}
                                            />
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DisplayPodImages),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
